import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, renderTextArea } from '../../shared/formComponents';
import AudienceFields from './shared/AudienceFields';
import MetaCustomAudienceField from './shared/MetaCustomAudienceField';
import MetaTargetingField from './shared/MetaTargetingField';
import GoogleConstantMultiSelect from './shared/GoogleConstantMultiSelect';
import {
  GOOGLE_SALES_COUNTRY_OPTIONS,
  META_GENDER_OPTIONS,
} from '../../shared/constants';
import {
  searchGoogleGeoConstants,
  getGoogleGeoConstantsByResourceNames,
  searchMetaCountries,
  getMetaCountriesByResourceNames,
} from '../../shared/budgetApi';

/**
 * Per-PLATFORM settings section for MPBT (one collapsible block per platform, not
 * per combo). Fields that apply to every selected combo of that platform (e.g.
 * Google's Sales Country/Geo Targeting) render once. Fields that only apply to a
 * specific Budget Type combo (e.g. Google Search's ASINs, Standard Shopping's
 * Product IDs) are called out inline with a "(Search only)"/"(Standard Shopping
 * only)" label and only render when that combo is selected.
 *
 * Landing Page URL, Brand Name, and Language are normalized ACROSS platforms
 * (Google + Meta) and rendered once in MultiPlatformBudgetToolForm itself, so they
 * are intentionally not repeated here.
 *
 * @param {string} platform - Platform key ('amazon' | 'google' | 'meta')
 * @param {Array<string>} selectedComboKeys - Selected combo keys for this platform
 * @param {string|number} accountId - Current account ID (needed for Meta's country lookup API)
 */
const MpbtPlatformSettingsSection = ({ platform, selectedComboKeys, accountId }) => {
  const hasCombo = (key) => selectedComboKeys.includes(key);
  // Amazon only has one combo today ("Item"), so its fields are namespaced under
  // that combo's key for consistency/future-proofing if more Amazon combos are added.
  const p = (comboKey, suffix) => `${comboKey}_${suffix}`;

  if (platform === 'amazon') {
    const comboKey = 'amazon_item';
    return (
      <>
        <div className="form__form-group">
          <span className="form__form-group-label">
            ON ASINs/SKUs <Tooltip id="amazonOn" msg="Items in this list will have their Ad Status turned ON" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field name={p(comboKey, 'masterItemOnList')} component={renderTextArea} />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            OFF ASINs/SKUs <Tooltip id="amazonOff" msg="Items in this list will have their Ad Status turned OFF" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field name={p(comboKey, 'masterItemOffList')} component={renderTextArea} />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            Portfolio IDs <Tooltip id="amazonPortfolioIds" msg="Comma-separated Amazon portfolio IDs" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field name={p(comboKey, 'portfolioIdsCsv')} component={renderFieldForm} placeholder="123456789,987654321" />
          </div>
        </div>
      </>
    );
  }

  if (platform === 'google') {
    return (
      <>
        <div className="form__form-group">
          <span className="form__form-group-label">Sales Country</span>
          <div className="form__form-group-field">
            <Field name="googleSalesCountry" component={renderSelectField} options={GOOGLE_SALES_COUNTRY_OPTIONS} />
          </div>
        </div>
        <Field
          name="googleGeoCountries"
          component={GoogleConstantMultiSelect}
          label="Target Countries (Optional)"
          tooltip="Search and select the countries to target for your Google ads"
          placeholder="Search countries (e.g., United States, Canada)..."
          searchFn={searchGoogleGeoConstants}
          lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
          extraSearchParams={{ uiCategory: 'COUNTRY' }}
        />
        <Field
          name="googleGeoStates"
          component={GoogleConstantMultiSelect}
          label="Target States (Optional)"
          tooltip="Search and select the states or regions to target for your Google ads"
          placeholder="Search states (e.g., California, Ontario)..."
          searchFn={searchGoogleGeoConstants}
          lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
          extraSearchParams={{ uiCategory: 'STATE' }}
        />
        <Field
          name="googleGeoCities"
          component={GoogleConstantMultiSelect}
          label="Target Cities (Optional)"
          tooltip="Search and select the cities to target for your Google ads"
          placeholder="Type at least 3 characters to search cities..."
          searchFn={searchGoogleGeoConstants}
          lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
          extraSearchParams={{ uiCategory: 'CITY' }}
          minSearchLength={3}
        />
        <Field
          name="googleGeoPostalCodes"
          component={GoogleConstantMultiSelect}
          label="Target Postal Codes (Optional)"
          tooltip="Search and select the postal codes to target for your Google ads"
          placeholder="Type at least 3 characters to search postal codes..."
          searchFn={searchGoogleGeoConstants}
          lookupByResourceNamesFn={getGoogleGeoConstantsByResourceNames}
          extraSearchParams={{ uiCategory: 'POSTAL_CODE' }}
          minSearchLength={3}
        />

        {hasCombo('google_search') && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              Target ASINs (Search only, Optional) <Tooltip id="googleAsins" msg="Comma-separated ASINs (must start with B0 and be 10 characters)" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field name={p('google_search', 'asins')} component={renderTextArea} placeholder="B0ABC123, B0DEF456" />
            </div>
          </div>
        )}

        {hasCombo('google_shop') && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              Product Item IDs (Standard Shopping only) <Tooltip id="googleProductIds" msg="Comma-separated Google Merchant Center product IDs" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field name={p('google_shop', 'productItemIds')} component={renderTextArea} placeholder="shopify_US_123_456" />
            </div>
          </div>
        )}
      </>
    );
  }

  if (platform === 'meta') {
    // Only one Meta combo today (Facebook Instagram) - all fields shared/apply to it.
    const comboKey = 'meta_facebook_instagram';
    return (
      <>
        <div className="form__form-group">
          <span className="form__form-group-label">Gender</span>
          <div className="form__form-group-field">
            <Field name={p(comboKey, 'gender')} component={renderSelectField} options={META_GENDER_OPTIONS} />
          </div>
        </div>
        <div className="form__form-group">
          <div style={{ width: '50%', float: 'left' }}>
            <span className="form__form-group-label">Min Age</span>
            <div className="form__form-group-field">
              <Field name={p(comboKey, 'ageMin')} component={renderFieldForm} placeholder="18" />
            </div>
          </div>
          <div style={{ marginLeft: '50%' }}>
            <span className="form__form-group-label">Max Age</span>
            <div className="form__form-group-field">
              <Field name={p(comboKey, 'ageMax')} component={renderFieldForm} placeholder="65" />
            </div>
          </div>
        </div>
        <Field
          name={p(comboKey, 'geoCountries')}
          component={GoogleConstantMultiSelect}
          label="Target Countries"
          tooltip="Search and select the countries to target for your Meta ads"
          placeholder="Search countries (e.g., United States, Canada)..."
          searchFn={searchMetaCountries}
          lookupByResourceNamesFn={getMetaCountriesByResourceNames}
          extraSearchParams={{ accountId }}
        />
        <div className="form__form-group">
          <span className="form__form-group-label">
            Product Set ID (Facebook Instagram only) <Tooltip id="metaProductSetId" msg="Meta product set ID for this budget" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field name={p(comboKey, 'productSetId')} component={renderFieldForm} />
          </div>
        </div>
        <AudienceFields
          name={p(comboKey, 'customAudienceIds')}
          label="Custom Audiences"
          tooltip="Add custom audience IDs to include in targeting"
          placeholder="Search or enter a custom audience ID"
          addButtonLabel="Add Audience"
          rowComponent={MetaCustomAudienceField}
          rowComponentProps={{ accountId }}
        />
        <AudienceFields
          name={p(comboKey, 'excludedCustomAudienceIds')}
          label="Excluded Custom Audiences"
          tooltip="Add custom audience IDs to exclude from targeting"
          placeholder="Search or enter a custom audience ID to exclude"
          addButtonLabel="Add Excluded Audience"
          rowComponent={MetaCustomAudienceField}
          rowComponentProps={{ accountId }}
        />
        <AudienceFields
          name={p(comboKey, 'income')}
          label="Income Targeting"
          tooltip="Add Meta income targeting IDs to include in flexible targeting"
          placeholder="Income targeting ID (e.g., 6107813079183)"
          addButtonLabel="Add Income Segment"
          rowComponent={MetaTargetingField}
          rowComponentProps={{ accountId, targetingType: 'income' }}
        />
        <AudienceFields
          name={p(comboKey, 'familyStatuses')}
          label="Family Status Targeting"
          tooltip="Add Meta family status targeting IDs to include in flexible targeting"
          placeholder="Family status targeting ID (e.g., 6023005681983)"
          addButtonLabel="Add Family Status"
          rowComponent={MetaTargetingField}
          rowComponentProps={{ accountId, targetingType: 'family_statuses' }}
        />
      </>
    );
  }

  return null;
};

MpbtPlatformSettingsSection.propTypes = {
  platform: PropTypes.oneOf(['amazon', 'google', 'meta']).isRequired,
  selectedComboKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MpbtPlatformSettingsSection.defaultProps = {
  accountId: null,
};

export default MpbtPlatformSettingsSection;
